/**
 * Alias Management Service
 * Handles forum alias operations
 */

import { apiFetch } from './http';

export interface AliasInfo {
  topicId: string;
  alias: string;
  updatedAt: string;
}

export interface SetAliasRequest {
  topicId: string;
  alias: string;
}

export interface CheckAliasResponse {
  available: boolean;
  message?: string;
}

/**
 * Get current user's aliases
 */
export async function getCurrentAliases(): Promise<AliasInfo[]> {
  try {
    const result = await apiFetch<AliasInfo[]>('/api/auth/aliases/current');
    return result;
  } catch (error) {
    console.error('[AliasService] Failed to fetch aliases:', error);
    // Return empty array instead of throwing to prevent UI crash
    return [];
  }
}

/**
 * Set or update alias for a topic
 */
export async function setAlias(topicId: string, alias: string): Promise<AliasInfo> {
  return apiFetch<AliasInfo>('/api/auth/aliases/activate', {
    method: 'POST',
    body: JSON.stringify({ topicId, alias }),
  });
}

/**
 * Check if an alias is available (unique)
 * This will attempt to query if alias exists
 */
export async function checkAliasAvailability(alias: string): Promise<boolean> {
  if (!alias || alias.trim().length === 0) {
    return false;
  }

  try {
    // Try to check if alias exists by attempting a lookup
    // Use apiFetch to include auth token
    const token = localStorage.getItem('auth_token');

    const response = await fetch(`/api/profile/${encodeURIComponent(alias)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });

    // If profile exists with this alias, it's taken
    if (response.ok) {
      return false;
    }

    // If 404, alias might be available
    // If 401/403, assume available (can't verify, but let backend validate on save)
    return response.status === 404 || response.status === 401 || response.status === 403;
  } catch (error) {
    console.error('Error checking alias availability:', error);
    // On error, assume available and let backend validate on save
    return true;
  }
}

/**
 * Get alias history
 */
export async function getAliasHistory(): Promise<any[]> {
  return apiFetch<any[]>('/api/auth/aliases/history');
}
